import "./RankMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

// ─────────────────────────────────────────────────────────
//  RANK MASTER COMPONENT
// ─────────────────────────────────────────────────────────
export default function RankMaster() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [selectedRank, setSelectedRank] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    description: "",
    active: 1,
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── GET /rank/list/all ───────────────────────────────────
  const getRanks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/rank/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      // Handle response - map API fields to component fields
      const data = response.data?.data || response.data || [];
      const mappedRanks = data.map((item) => ({
        id: item.id || item.rank_id,
        description: item.description,
        active: item.is_active === true || item.is_active === 1,
      }));

      setRanks(mappedRanks);
    } catch (err) {
      console.error("Failed to load ranks:", err.response?.data);
      toastError("Failed to load ranks");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    getRanks();
  }, [getRanks]);

  // ─── GET /rank/{rank_id} ───────────────────────────────────
  const getRankById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/rank/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;
        return {
          id: data.id || data.rank_id,
          description: data.description,
          active: data.is_active === true || data.is_active === 1,
        };
      } catch (err) {
        console.error("Failed to fetch rank:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── POST /rank/add ───────────────────────────────────
  const createRank = async (rankData) => {
    try {
      const response = await axios.post(
        `${API_URL}/rank/add`,
        {
          description: rankData.description,
          is_active: rankData.active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create error:", err.response?.data);
      throw err;
    }
  };

  // ─── PUT /rank/update/{rank_id} ────────────────────────────
  const updateRank = async (id, rankData) => {
    try {
      const response = await axios.put(
        `${API_URL}/rank/update/${id}`,
        {
          description: rankData.description,
          is_active: rankData.active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  // ─── Open Edit ───────────────────────────────────
  const openEdit = async (rank) => {
    setFormError("");
    setIsEditMode(true);
    // setSelectedRank(rank);
    setEditingId(rank.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const rankData = await getRankById(rank.id);
      setFormData({
        description: rankData.description || "",
        active: rankData.active ? 1 : 0,
      });
    } catch (err) {
      const msg = "Failed to load rank details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const openAdd = () => {
    setIsEditMode(false);
    // setSelectedRank(null);
    setEditingId(null);
    setFormData({ description: "", active: 1 });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData({ description: "", active: 1 });
    setFormError("");
    // setSelectedRank(null);
    setEditingId(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
          ? 1
          : 0
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  // ─── Handle Submit (Create/Update) ───────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");
    if (!formData.description.trim()) {
      const msg = "Rank description is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    setFormLoading(true);

    try {
      if (isEditMode) {
        // Update existing rank
        await updateRank(editingId, formData);

        // Update local state
        setRanks((prevRanks) =>
          prevRanks.map((r) =>
            r.id === editingId
              ? {
                  ...r,
                  description: formData.description,
                  active: formData.active === 1,
                }
              : r,
          ),
        );

        toastSuccess("Rank updated successfully.");
      } else {
        // Create new rank
        await createRank(formData);

        // Refresh the list
        await getRanks();

        toastSuccess("Rank created successfully.");
      }

      closeForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save rank.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Toggle Rank Status ──────────────────────────────
  const toggleRankStatus = async (rank) => {
    const newStatus = rank.active ? 0 : 1;

    try {
      await updateRank(rank.id, {
        description: rank.description,
        active: newStatus,
      });

      // Update local state
      setRanks((prev) =>
        prev.map((r) =>
          r.id === rank.id ? { ...r, active: newStatus === 1 } : r,
        ),
      );

      toastSuccess(
        newStatus === 1
          ? "Rank activated successfully."
          : "Rank deactivated successfully.",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update rank status.";
      toastError(msg);
    }
  };

  // ── Filter ranks ──
  const filteredRanks = ranks.filter((rank) =>
    rank.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ════════════════════════════════════════════════════
  //  ADD / EDIT FORM
  // ════════════════════════════════════════════════════
  if (showForm) {
    return (
      <div className="rm-form-page">
        {/* Header with Back Arrow */}
        <div className="rm-form-header">
          <div className="rm-form-header-left">
            <button className="rm-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="rm-back-icon" />
            </button>
            <div>
              <h2 className="rm-form-title">
                {isEditMode ? "Edit Rank" : "Add Rank"}
              </h2>
              <p className="rm-form-subtitle">
                {isEditMode
                  ? "Update rank description and status"
                  : "Create a new member rank classification"}
              </p>
            </div>
          </div>
        </div>

        <div className="rm-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="rm-form-grid">
            {/* Description */}
            <div className="rm-form-group">
              <label>Rank Description *</label>
              <input
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter rank description"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>

            {/* Status — only show in edit mode */}
            {isEditMode && (
              <div className="rm-form-group">
                <label>Status</label>
                <div className="rm-status-toggle">
                  <label className="rm-switch">
                    <input
                      type="checkbox"
                      checked={formData.active === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          active: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <span className="rm-slider"></span>
                  </label>
                  <span className="rm-status-label">
                    {formData.active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="rm-form-footer">
            <button className="rm-btn rm-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="rm-btn rm-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Rank"
                  : "Save Rank"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  RANK LIST
  // ════════════════════════════════════════════════════
  return (
    <div className="rm-list-page">
      {/* ─── Top Bar ─── */}
      <div className="rm-page-header">
        <div>
          <h1 className="rm-page-title">Rank Master</h1>
          <p className="rm-page-subtitle">
            Manage member ranks and classifications
          </p>
        </div>
        <div className="rm-page-actions">
          <div className="rm-search-box">
            <span className="rm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search ranks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="rm-btn rm-btn-primary" onClick={openAdd}>
            + Add Rank
          </button>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="rm-table-card">
        <div className="rm-table-responsive">
          <table className="rm-table">
            <thead>
              <tr>
                <th className="rm-col-id">#</th>
                <th className="rm-col-description">Description</th>
                <th className="rm-col-active">Status</th>
                <th className="rm-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="rm-empty-row">
                    Loading ranks...
                  </td>
                </tr>
              ) : filteredRanks.length === 0 ? (
                <tr>
                  <td colSpan="4">
                    <div className="rm-empty-state">
                      <div className="rm-empty-icon">📋</div>
                      <div className="rm-empty-title">No Ranks Found</div>
                      <div className="rm-empty-desc">
                        {searchTerm
                          ? `No ranks match "${searchTerm}"`
                          : "Start by adding your first rank"}
                      </div>
                      {/* {!searchTerm && (
                        <button
                          className="rm-btn rm-btn-primary"
                          onClick={openAdd}
                        >
                          + Add Rank
                        </button>
                      )} */}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRanks.map((rank, index) => (
                  <tr key={rank.id}>
                    <td className="rm-col-id">{index + 1}</td>
                    <td className="rm-col-description">{rank.description}</td>
                    <td className="rm-col-active">
                      <label className="rm-switch">
                        <input
                          type="checkbox"
                          checked={rank.active}
                          onChange={() => toggleRankStatus(rank)}
                        />
                        <span className="rm-slider"></span>
                      </label>
                    </td>
                    <td className="rm-col-actions">
                      <div className="rm-action-group">
                        <button
                          className="rm-action-btn rm-action-btn-edit"
                          onClick={() => openEdit(rank)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="rm-action-icon"
                          />
                        </button>
                        {/* <button
                          className="rm-action-btn rm-action-btn-delete"
                          onClick={() => handleDelete(rank)}
                          title="Delete"
                        >
                          🗑️
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
